import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ChatList from '../components/ChatList';
import ChatRoom from '../components/ChatRoom';
import { useAuth } from '../hooks/useAuth';
import { getAccessToken, refreshToken } from '../services/authService';
import chatService from '../services/chatService';

const Chat: React.FC = () => {
  const { isAuthenticated, isLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [wsInitialized, setWsInitialized] = useState(false);

  // Fast initial check - don't wait for async operations if token exists
  const hasToken = !!getAccessToken();

  // Initialize WebSocket once when component mounts, don't depend on isAuthenticated
  useEffect(() => {
    const initWs = async () => {
      // Only initialize once
      if (wsInitialized) return;
      
      const token = getAccessToken();
      if (token) {
        console.log('Chat: Initializing WebSocket connection');
        chatService.initializeWebSocket(token, () => {
          console.log('Chat: WebSocket connected successfully');
        });
        
        // Setup heartbeat to keep connection alive
        chatService.setupHeartbeat();
        
        // Setup automatic reconnection
        chatService.setupReconnection();
        
        setWsInitialized(true);
      }
    };

    initWs();
    
    return () => {
      console.log('Chat: Cleaning up WebSocket connections');
      chatService.disconnect();
    };
  }, [wsInitialized]);

  // Check and refresh token on page load - but don't block rendering
  useEffect(() => {
    const checkAuth = async () => {
      console.log('Chat: Checking authentication token');
      if (!getAccessToken()) {
        try {
          console.log('Chat: No token found, attempting to refresh');
          const newToken = await refreshToken();
          if (newToken) {
            console.log('Chat: Token refreshed successfully');
            await refresh();
            // Initialize WebSocket if it wasn't already
            if (!wsInitialized) {
              chatService.initializeWebSocket(newToken);
              setWsInitialized(true);
            }
          } else {
            console.log('Chat: Token refresh returned no token');
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
        }
      } else {
        console.log('Chat: Token already exists');
      }
    };

    checkAuth();
  }, [refresh, wsInitialized]);

  // Only show loading spinner if we have no token AND auth is still loading
  if (!hasToken && isLoading) {
    console.log('Chat: No token and auth is loading, showing spinner');
    return (
      <div className="p-4 flex items-center justify-center h-[calc(100vh-64px)] pt-[64px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // If we have a token, render the chat even if auth state is still loading
  // This allows for faster initial render
  if (!hasToken && !isAuthenticated) {
    console.log('Chat: Not authenticated, redirecting to signin');
    return <Navigate to="/signin" />;
  }

  console.log('Chat: Rendering chat component');
  return (
    <div className="max-w-5xl mx-auto h-[calc(100vh-64px)] mt-16">
      <div className="bg-white rounded-lg shadow-md h-full border border-gray-200 overflow-hidden">
        <Routes>
          <Route path="/" element={<ChatList />} />
          <Route path="/:chatRoomId" element={<ChatRoom />} />
        </Routes>
      </div>
    </div>
  );
};

export default Chat;
